<script setup lang="ts">
/*
 * Выбор исполнителей: сотрудники / отделения / должности.
 * Режимы — pill слева от панели, как в макете Figma.
 */

import axios from 'axios'
import AnimatedLogoLoader from '@/components/createdUi/AnimatedLogoLoader.vue'
import CustomSelectedItems from '@/components/createdUi/CustomSelectedItems.vue'
import { getApiError } from '@/composables/getApiError'
import { BASE_URL } from '@/environment/environment'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import type { IEmployeeTreeDepartment } from '@/modules/apiStore/types/employees/IEmployeeTree'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { PARTICIPANT_MODE_ITEMS } from '@/components/createdUi/participantSelectionModes'
import type { TParticipantSelectionMode } from '@/components/createdUi/participantSelectionModes'

export type { TParticipantSelectionMode }

export interface IParticipantEmployee {
  id: string
  full_name: string
  position_name?: string
  avatar?: string
}

export interface IParticipantNamedItem {
  id: string
  name: string
}

export type TParticipantItem = IParticipantEmployee | IParticipantNamedItem

export interface IParticipantSelectionResult {
  employees: IParticipantEmployee[]
  departments: IParticipantNamedItem[]
  positions: IParticipantNamedItem[]
}

interface IDepartmentTreeNode {
  id: string
  name: string
  short_name?: string | null
  children: IDepartmentTreeNode[]
}

interface IListRow {
  id: string
  title: string
  subtitle?: string
  raw: TParticipantItem
}

interface Props {
  selectedEmployees?: IParticipantEmployee[]
  selectedDepartments?: IParticipantNamedItem[]
  selectedPositions?: IParticipantNamedItem[]
  title?: string
}

const props = withDefaults(defineProps<Props>(), {
  selectedEmployees: () => [],
  selectedDepartments: () => [],
  selectedPositions: () => [],
  title: 'Выбор исполнителей',
})

const emit = defineEmits<{
  (e: 'select', payload: IParticipantSelectionResult): void
  (e: 'close'): void
}>()

const feedbackStore = useFeedbackStore()
const apiStore = useApiStore()

const search = ref('')
const searchDebounced = refDebounced(search, 300)
const isOpen = ref(false)
const isLoading = ref(false)
const showLocalLoader = computed(() => isLoading.value && !feedbackStore.isGlobalLoading)
const currentMode = ref<TParticipantSelectionMode>('employees')

const employeesList = ref<IParticipantEmployee[]>([])
const departmentsTree = ref<IDepartmentTreeNode[]>([])
const serverDepartmentsTree = ref<IDepartmentTreeNode[] | null>(null)
const departmentSearchLoading = ref(false)
let departmentSearchSeq = 0
const positionsList = ref<IParticipantNamedItem[]>([])
const openedDepts = ref<string[]>([])

const localEmployees = ref<IParticipantEmployee[]>([])
const localDepartments = ref<IParticipantNamedItem[]>([])
const localPositions = ref<IParticipantNamedItem[]>([])

const isDepartmentMode = computed(() => currentMode.value === 'departments')

const hideModal = () => {
  setTimeout(() => {
    isOpen.value = false
  }, 0)
}

const highlightMatch = (text: string, searchTerm: string) => {
  if (!searchTerm || !text) return text
  const safe = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const re = new RegExp(`(${safe})`, 'gi')
  return text.replace(re, '<mark class="search-highlight">$1</mark>')
}

const resetSearch = () => {
  search.value = ''
}

const isDummyDept = (item: { id: string }) => String(item.id).startsWith('dummy-')

const departmentDisplayName = (node: { name: string; short_name?: string | null }) => {
  return node.short_name?.trim() ? node.short_name : node.name
}

const normalizeDepartmentTree = (nodes: IDepartmentTreeNode[]): IDepartmentTreeNode[] => {
  return nodes.map(node => ({
    id: String(node.id),
    name: departmentDisplayName(node),
    children: Array.isArray(node.children) ? normalizeDepartmentTree(node.children) : [],
  }))
}

const findDepartment = (nodes: IDepartmentTreeNode[], id: string): IDepartmentTreeNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node
    const nested = findDepartment(node.children, id)
    if (nested) return nested
  }
  return null
}

const collectDepartmentBranch = (dept: IDepartmentTreeNode): IParticipantNamedItem[] => {
  const list: IParticipantNamedItem[] = [{ id: dept.id, name: dept.name }]
  for (const child of dept.children) {
    if (!isDummyDept(child))
      list.push(...collectDepartmentBranch(child))
  }
  return list
}

const collectDepartmentIds = (dept: IDepartmentTreeNode): string[] => {
  const ids = [dept.id]
  for (const child of dept.children) {
    if (!isDummyDept(child))
      ids.push(...collectDepartmentIds(child))
  }
  return ids
}

const currentSelected = computed<TParticipantItem[]>(() => {
  if (currentMode.value === 'employees') return localEmployees.value
  if (currentMode.value === 'departments') return localDepartments.value
  return localPositions.value
})

const selectedIds = computed(() => new Set(currentSelected.value.map(item => String(item.id))))

const applySelectedFromProps = () => {
  localEmployees.value = [...props.selectedEmployees]
  localDepartments.value = [...props.selectedDepartments]
  localPositions.value = [...props.selectedPositions]
}

const flattenEmployees = (depts: IEmployeeTreeDepartment[]): IParticipantEmployee[] => {
  const list: IParticipantEmployee[] = []
  const walk = (nodes: IEmployeeTreeDepartment[]) => {
    for (const node of nodes) {
      for (const emp of node.employees ?? []) {
        list.push({
          id: String(emp.id),
          full_name: emp.full_name,
          position_name: emp.position_name,
        })
      }
      if (node.children?.length)
        walk(node.children)
    }
  }
  walk(depts)
  return list
}

const fetchEmployees = async () => {
  if (employeesList.value.length) return
  if (!apiStore.employees?.length)
    await apiStore.fetchAllEmployees({ silent: true })
  employeesList.value = flattenEmployees(apiStore.employees ?? [])
}

const fetchDepartments = async () => {
  if (departmentsTree.value.length) return
  const response = await axios.get(`${BASE_URL}/api/employee/department/`)
  const results = response.data.results ?? response.data
  const tree = Array.isArray(results) ? results as IDepartmentTreeNode[] : []
  departmentsTree.value = normalizeDepartmentTree(tree)
}

const fetchPositions = async () => {
  if (positionsList.value.length) return
  const response = await axios.get(`${BASE_URL}/api/employee/position/`)
  const results = response.data.results ?? response.data
  positionsList.value = Array.isArray(results) ? results : []
}

const loadModeItems = async () => {
  isLoading.value = true
  try {
    if (currentMode.value === 'employees')
      await fetchEmployees()
    else if (currentMode.value === 'departments')
      await fetchDepartments()
    else
      await fetchPositions()
  } catch (error) {
    console.error(error)
    feedbackStore.showToast({
      type: 'error',
      message: getApiError(error, 'Не удалось загрузить список'),
      time: 7000,
    })
  } finally {
    isLoading.value = false
  }
}

const sourceRows = computed<IListRow[]>(() => {
  if (currentMode.value === 'employees') {
    return employeesList.value.map(item => ({
      id: String(item.id),
      title: item.full_name,
      subtitle: item.position_name,
      raw: item,
    }))
  }

  return positionsList.value.map(item => ({
    id: String(item.id),
    title: item.name,
    raw: item,
  }))
})

const filteredRows = computed(() => {
  const query = searchDebounced.value.trim().toLowerCase()
  if (!query) return sourceRows.value

  return sourceRows.value.filter(row => {
    if (row.title.toLowerCase().includes(query)) return true
    return Boolean(row.subtitle?.toLowerCase().includes(query))
  })
})

const isDepartmentSearchActive = computed(() => {
  return isDepartmentMode.value && searchDebounced.value.trim().length > 0
})

const filteredDepartmentsTree = computed<IDepartmentTreeNode[]>(() => {
  if (isDepartmentSearchActive.value)
    return serverDepartmentsTree.value ?? []

  if (!departmentsTree.value.length) return []

  const makeLazy = (dept: IDepartmentTreeNode): IDepartmentTreeNode => {
    const isOpened = openedDepts.value.includes(dept.id)
    if (!isOpened) {
      return {
        ...dept,
        children: dept.children.length
          ? [{ id: `dummy-${dept.id}`, name: '', children: [] }]
          : [],
      }
    }
    return {
      ...dept,
      children: dept.children.map(child => makeLazy(child)),
    }
  }
  return departmentsTree.value.map(dept => makeLazy(dept))
})

watch([searchDebounced, currentMode], async ([query, mode]) => {
  if (mode !== 'departments') {
    departmentSearchSeq += 1
    departmentSearchLoading.value = false
    return
  }

  const trimmed = query.trim()
  if (!trimmed) {
    departmentSearchSeq += 1
    serverDepartmentsTree.value = null
    departmentSearchLoading.value = false
    openedDepts.value = []
    return
  }

  const seq = ++departmentSearchSeq
  departmentSearchLoading.value = true
  try {
    const response = await axios.get(`${BASE_URL}/api/employee/department/`, {
      params: { search: trimmed },
    })
    if (seq !== departmentSearchSeq)
      return
    const results = response.data.results ?? response.data
    const tree = Array.isArray(results) ? normalizeDepartmentTree(results as IDepartmentTreeNode[]) : []
    serverDepartmentsTree.value = tree
    const opened: string[] = []
    const collect = (nodes: IDepartmentTreeNode[]) => {
      for (const node of nodes) {
        opened.push(node.id)
        if (node.children.length)
          collect(node.children)
      }
    }
    collect(tree)
    openedDepts.value = opened
  }
  catch (error) {
    if (seq !== departmentSearchSeq)
      return
    serverDepartmentsTree.value = []
    feedbackStore.showToast({
      type: 'error',
      message: getApiError(error, 'Не удалось выполнить поиск'),
      time: 5000,
    })
  }
  finally {
    if (seq === departmentSearchSeq)
      departmentSearchLoading.value = false
  }
})

const isSelected = (id: string) => selectedIds.value.has(id)

const handleSelect = (row: IListRow) => {
  const id = row.id

  if (currentMode.value === 'employees') {
    if (selectedIds.value.has(id))
      localEmployees.value = localEmployees.value.filter(item => String(item.id) !== id)
    else
      localEmployees.value = [...localEmployees.value, row.raw as IParticipantEmployee]
    return
  }

  if (selectedIds.value.has(id))
    localPositions.value = localPositions.value.filter(item => String(item.id) !== id)
  else
    localPositions.value = [...localPositions.value, row.raw as IParticipantNamedItem]
}

const handleDeptClick = (event: Event, item: IDepartmentTreeNode) => {
  event.stopPropagation()
  if (isDummyDept(item)) return

  const source = findDepartment(departmentsTree.value, String(item.id))
  if (!source) return

  const branch = collectDepartmentBranch(source)
  const allSelected = branch.every(node => selectedIds.value.has(node.id))

  if (allSelected) {
    const ids = new Set(branch.map(node => node.id))
    localDepartments.value = localDepartments.value.filter(item => !ids.has(item.id))
    return
  }

  const existing = new Set(localDepartments.value.map(item => item.id))
  for (const node of branch) {
    if (!existing.has(node.id))
      localDepartments.value.push(node)
  }

  for (const id of collectDepartmentIds(source)) {
    if (!openedDepts.value.includes(id))
      openedDepts.value.push(id)
  }
}

const selectedGroups = computed(() => {
  const groups: {
    key: TParticipantSelectionMode
    title: string
    hideAvatar: boolean
    items: { id: string; name: string; description?: string; avatar?: string }[]
  }[] = []

  if (localEmployees.value.length) {
    groups.push({
      key: 'employees',
      title: 'Сотрудники',
      hideAvatar: false,
      items: localEmployees.value.map(item => ({
        id: item.id,
        name: item.full_name,
        description: item.position_name,
        avatar: item.avatar,
      })),
    })
  }

  if (localDepartments.value.length) {
    groups.push({
      key: 'departments',
      title: 'Отделения',
      hideAvatar: true,
      items: localDepartments.value.map(item => ({
        id: item.id,
        name: item.name,
      })),
    })
  }

  if (localPositions.value.length) {
    groups.push({
      key: 'positions',
      title: 'Должности',
      hideAvatar: true,
      items: localPositions.value.map(item => ({
        id: item.id,
        name: item.name,
      })),
    })
  }

  return groups
})

const totalSelectedCount = computed(() =>
  localEmployees.value.length + localDepartments.value.length + localPositions.value.length,
)

const removeItem = (id: string | number, groupKey?: string) => {
  const idStr = String(id)
  const mode = groupKey || currentMode.value

  if (mode === 'employees')
    localEmployees.value = localEmployees.value.filter(item => String(item.id) !== idStr)
  else if (mode === 'departments')
    localDepartments.value = localDepartments.value.filter(item => String(item.id) !== idStr)
  else
    localPositions.value = localPositions.value.filter(item => String(item.id) !== idStr)
}

const removeGroup = (groupKey: string) => {
  if (groupKey === 'employees')
    localEmployees.value = []
  else if (groupKey === 'departments')
    localDepartments.value = []
  else
    localPositions.value = []
}

const removeAllItems = () => {
  if (currentMode.value === 'employees')
    localEmployees.value = []
  else if (currentMode.value === 'departments')
    localDepartments.value = []
  else
    localPositions.value = []
}

const clearAllSelections = () => {
  localEmployees.value = []
  localDepartments.value = []
  localPositions.value = []
}

const applySelection = () => {
  emit('select', {
    employees: [...localEmployees.value],
    departments: [...localDepartments.value],
    positions: [...localPositions.value],
  })
  hideModal()
  resetSearch()
}

const closeModal = () => {
  hideModal()
  resetSearch()
  emit('close')
}

const switchMode = async (mode: TParticipantSelectionMode) => {
  if (currentMode.value === mode) return
  currentMode.value = mode
  openedDepts.value = []
  resetSearch()
  await loadModeItems()
}

const openModal = async (mode: TParticipantSelectionMode = 'employees') => {
  currentMode.value = mode
  openedDepts.value = []
  applySelectedFromProps()
  isOpen.value = true
  await loadModeItems()
}

const countLabel = computed(() => `Выбрано: ${totalSelectedCount.value}`)

const emptyLabel = computed(() => {
  if (currentMode.value === 'employees') return 'Сотрудники не найдены'
  if (currentMode.value === 'departments') return 'Отделения не найдены'
  return 'Должности не найдены'
})

const placeholder = 'Поиск'

defineExpose({
  open: openModal,
  close: closeModal,
})
</script>

<template>
  <VDialog
    v-model="isOpen"
    :max-width="600"
    max-height="90vh"
    content-class="entity-form-menu participant-picker-menu"
    @click:outside="closeModal"
  >
    <div class="participant-picker position-relative w-100">
      <div class="participant-mode-rail">
        <button
          v-for="item in PARTICIPANT_MODE_ITEMS"
          :key="item.value"
          type="button"
          class="participant-mode-tab"
          :class="{ 'participant-mode-tab--active': currentMode === item.value }"
          @click="switchMode(item.value)"
        >
          <VIcon
            :icon="item.icon"
            size="20"
            color="inherit"
          />
          <span>{{ item.title }}</span>
        </button>
      </div>

      <VCard class="pa-4 d-flex flex-column ga-2 w-100 min-h-0">
        <div class="d-flex align-center justify-space-between">
          <span class="text-h4">{{ title }}</span>
          <VBtn
            icon
            variant="text"
            size="small"
            @click="closeModal"
          >
            <VIcon icon="ri-close-line" />
          </VBtn>
        </div>

        <div class="d-flex flex-column ga-1">
          <VTextField
            v-model="search"
            variant="outlined"
            clearable
            :append-inner-icon="search ? undefined : 'ri-search-line'"
            autofocus
            :placeholder="placeholder"
            rounded="pill"
            class="search-field"
            @click:clear="resetSearch"
          />

          <div :style="{ visibility: totalSelectedCount === 0 ? 'hidden' : 'visible' }">
            <VMenu
              open-on-hover
              :close-on-content-click="false"
              close-delay="200"
              location="bottom start"
              offset="8"
              content-class="date-picker-menu"
            >
              <template #activator="{ props: menuProps }">
                <span
                  v-bind="menuProps"
                  class="text-body-1 text-medium-emphasis cursor-pointer d-inline-block"
                >
                  {{ countLabel }}
                </span>
              </template>

              <CustomSelectedItems
                :groups="selectedGroups"
                :title="`Выбрано ${totalSelectedCount}:`"
                @remove="removeItem"
                @remove-all="clearAllSelections"
                @remove-group="removeGroup"
              />
            </VMenu>
          </div>
        </div>

        <div
          class="participant-picker-list flex-grow-1 overflow-y-auto min-h-0"
          :class="{ 'custom-treeview-container': isDepartmentMode }"
        >
          <div
            v-if="isLoading || departmentSearchLoading"
            class="participant-picker-loader d-flex align-center justify-center"
          >
            <AnimatedLogoLoader />
          </div>
          <VTreeview
            v-else-if="isDepartmentMode && filteredDepartmentsTree.length"
            v-model:opened="openedDepts"
            :items="filteredDepartmentsTree"
            item-children="children"
            item-title="name"
            item-value="id"
            :open-all="isDepartmentSearchActive"
            :open-on-click="false"
            class="custom-treeview"
          >
            <template #toggle="{ props: toggleProps, item }">
              <VBtn
                v-if="item.children?.length && !isDummyDept(item)"
                v-bind="toggleProps"
                variant="text"
                density="compact"
                icon
                class="expand-btn"
                :class="{ expanded: openedDepts.some(id => String(id) === String(item.id)) }"
              >
                <VIcon
                  icon="ri-play-fill"
                  size="16"
                  class="expand-icon"
                />
              </VBtn>
              <div v-else class="node-leaf-spacer" />
            </template>

            <template #title="{ item }">
              <div
                v-if="item && !isDummyDept(item)"
                class="w-100 text-body-1 py-1 px-2 rounded-md cursor-pointer"
                :class="isSelected(item.id) ? 'selected-employee text-primary' : 'text-high-emphasis'"
                @click="handleDeptClick($event, item)"
                v-html="highlightMatch(item.name, searchDebounced)"
              />
            </template>
          </VTreeview>

          <VList v-else-if="!isDepartmentMode && filteredRows.length" class="pa-0">
            <VListItem
              v-for="row in filteredRows"
              :key="row.id"
              class="rounded-md px-2"
              :class="isSelected(row.id) ? 'selected-employee text-primary' : 'text-high-emphasis'"
              @click="handleSelect(row)"
            >
              <VListItemTitle class="text-body-1 text-high-emphasis">
                <span v-html="highlightMatch(row.title, searchDebounced)" />
              </VListItemTitle>
              <VListItemSubtitle v-if="row.subtitle" class="text-subtitle-2 text-medium-emphasis">
                <span v-html="highlightMatch(row.subtitle, searchDebounced)" />
              </VListItemSubtitle>
            </VListItem>
          </VList>

          <div
            v-else
            class="d-flex flex-column align-center justify-center ga-2 pa-8 text-medium-emphasis h-100"
          >
            <VIcon icon="ri-search-line" size="48" />
            <div class="text-body-1">
              {{ emptyLabel }}
            </div>
            <div class="text-caption text-medium-emphasis">
              Попробуйте изменить поисковый запрос
            </div>
          </div>
        </div>

        <div class="d-flex ga-3">
          <VBtn
            variant="outlined"
            color="secondary"
            class="flex-grow-1 text-none"
            @click="removeAllItems"
          >
            Очистить
          </VBtn>
          <VBtn
            color="primary"
            class="flex-grow-1 text-none"
            @click="applySelection"
          >
            Выбрать
          </VBtn>
        </div>
      </VCard>
    </div>
  </VDialog>
</template>

<style scoped lang="scss">
:deep(.search-highlight) {
  background-color: rgba(var(--v-theme-primary), var(--v-activated-opacity));
  padding: 2px 0;
  border-radius: 2px;
  color: inherit;
}

:deep(.search-field.v-text-field .v-field) {
  border-radius: 500px !important;
}

.selected-employee {
  background: rgba(var(--v-theme-primary), var(--v-activated-opacity)) !important;
  border-radius: 6px !important;
}

.custom-treeview {
  --indent-level: 0;

  :deep(.v-list-item-title),
  :deep(.v-list-item__content) {
    white-space: normal !important;
    word-break: break-word !important;
  }

  :deep(.v-list-item) {
    padding-inline-start: calc(var(--indent-level) * 34px) !important;
    border-radius: 6px !important;
    height: auto !important;
    min-height: 40px !important;
    align-items: center !important;
  }

  :deep(.v-treeview-group.v-list-group .v-list-group__items .v-list-item) {
    padding-inline-start: calc(var(--indent-level) * 34px) !important;
  }

  :deep(.v-list-group__items) {
    --indent-level: 1;
  }

  :deep(.v-list-group__items .v-list-group__items) {
    --indent-level: 2;
  }

  :deep(.v-list-group__items .v-list-group__items .v-list-group__items) {
    --indent-level: 3;
  }

  :deep(.v-list-item__prepend) {
    width: 24px !important;
    min-width: 24px !important;
    margin-inline-end: 10px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  :deep(.v-list-item-action) {
    margin: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 100% !important;
    height: 100% !important;
  }

  :deep(.v-list-item__spacer) {
    display: none !important;
  }
}

.custom-treeview-container {
  padding-inline-end: 8px;
}

.node-leaf-spacer {
  width: 24px !important;
  min-width: 24px !important;
  height: 24px !important;
}

.expand-btn {
  width: 24px !important;
  height: 24px !important;
  min-width: 24px !important;
  padding: 0 !important;

  .v-icon {
    width: 16px;
    height: 16px;
    transition: transform 0.2s ease;
    transform: rotate(0deg);
  }

  &.expanded .v-icon {
    transform: rotate(90deg) !important;
  }
}

.participant-picker-list {
  height: 360px;
}

.participant-picker-loader {
  height: 100%;
}

.participant-picker :deep(.v-card) {
  position: relative;
  z-index: 1;
  max-height: 90vh;
}

.participant-mode-rail {
  --petal-under: 36px;

  position: absolute;
  inset-inline-end: 100%;
  inset-block-start: 70px;
  z-index: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.participant-mode-tab {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
  padding-block: 12px;
  padding-inline-start: 12px;
  padding-inline-end: 40px;
  margin-inline-end: calc(var(--petal-under) * -1);
  border: none;
  border-radius: 100px;
  background: rgb(var(--v-theme-grey-100));
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.25s ease, color 0.25s ease;

  &--active {
    background: rgb(var(--v-theme-primary));
    color: rgb(var(--v-theme-on-primary));
    font-size: 15px;
    line-height: 22px;
  }
}

.v-theme--dark .participant-mode-tab:not(.participant-mode-tab--active) {
  background: rgb(var(--v-tooltip-background));
  color: rgba(var(--v-theme-surface), var(--v-medium-emphasis-opacity));
}
</style>

<style lang="scss">
.v-overlay__content.participant-picker-menu {
  overflow: visible !important;
}
</style>
